import { compareSpreadsheetColumnIds as areSpreadsheetColumnIdsEqual } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import { copy } from '~/_shared/utils/collections/collections';
import { createUuid } from '~/_shared/utils/createUuid';
import { stringObjectEntries } from '~/_shared/utils/object/objectEnumerators';
import type { MapSettingsChartsAction } from './mapSettingsCharts.action';
import {
  mapSettingsChartsAddOrUpdateChart, mapSettingsChartsRemoveCharts,
} from './mapSettingsCharts.actionCreators';
import type { MapSettingsChartsState } from './mapSettingsCharts.state';
import {
  type Chart, type ChartWithRefMetrics,
  isChartMetricSpreadsheetColumn,
} from './mapSettingsCharts.types';

export const mapSettingsChartsInitialState: MapSettingsChartsState = {
  columnsMap: {},
  items: [],
};

export const mapSettingsChartsReducer = (
  state = mapSettingsChartsInitialState,
  action: MapSettingsChartsAction
): MapSettingsChartsState => {

  if (mapSettingsChartsAddOrUpdateChart.match(action)) {
    const { columnsMap, chart } = updateColumnsMapAndReplaceColumnIdsWithRefs(
      state.columnsMap,
      action.payload.chart
    );

    const existingIndex = state.items.findIndex(chart => chart.id === action.payload.chart.id);
    if (existingIndex === -1) {
      return {
        ...state,
        columnsMap,
        items: [...state.items, chart],
      };
    }

    return {
      ...state,
      columnsMap,
      items: copy.andReplace(state.items, existingIndex, chart),
    };
  }

  if (mapSettingsChartsRemoveCharts.match(action)) {
    const newItems = state.items.filter(chart => !action.payload.ids.has(chart.id));

    return {
      ...state,
      items: newItems,
    };
  }

  return state;
};

const updateColumnsMapAndReplaceColumnIdsWithRefs = (
  columnsMap: MapSettingsChartsState['columnsMap'],
  chart: Chart,
): {
  columnsMap: MapSettingsChartsState['columnsMap'];
  chart: ChartWithRefMetrics;
} => {
  const replacedMetrics = chart.metrics.reduce((accum, metric) => {
    if (isChartMetricSpreadsheetColumn(metric)) {
      const columnRefIdFromExisting = stringObjectEntries(columnsMap)
        .find(([_, spreadsheetColumnId]) => areSpreadsheetColumnIdsEqual(spreadsheetColumnId, metric.column))?.[0];

      let finalColumnIdRef = columnRefIdFromExisting;
      if (!finalColumnIdRef) {
        finalColumnIdRef = createUuid();
        accum.columnsMap = {
          ...accum.columnsMap,
          [finalColumnIdRef]: metric.column,
        };
      }

      accum.metrics = [
        ...accum.metrics,
        {
          ...metric,
          column: finalColumnIdRef,
        } satisfies ChartWithRefMetrics['metrics'][number],
      ];
    }
    else {
      accum.metrics = [
        ...accum.metrics,
        metric,
      ];
    }
    return accum;
  }, {
    columnsMap,
    metrics: [] as ChartWithRefMetrics['metrics'],
  });

  return {
    columnsMap: replacedMetrics.columnsMap,
    chart: {
      ...chart,
      metrics: replacedMetrics.metrics,
    } satisfies ChartWithRefMetrics,
  };
};
